// MonthlyTaskReport.js - Fixed Version with No Backend Dependencies
import React, { useState, useEffect } from 'react';
import './MonthlyTaskReport.css';

const MonthlyTaskReport = ({ onClose }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [reportType, setReportType] = useState('comprehensive');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    department: 'all'
  });

  useEffect(() => {
    fetchEmployees();
    fetchAllTasks();
  }, []);

  function getCurrentMonth() {
    return new Date().getMonth() + 1; // 1-12
  }

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/admin/users`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const data = await response.json();
      if (data.success) {
        setEmployees(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      // Set dummy employees if API fails
      setEmployees([
        { _id: '1', name: 'John Doe', email: 'john@example.com', department: 'IT' },
        { _id: '2', name: 'Jane Smith', email: 'jane@example.com', department: 'HR' },
        { _id: '3', name: 'Mike Johnson', email: 'mike@example.com', department: 'IT' }
      ]);
    }
  };

  const fetchAllTasks = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      // Try multiple endpoints to get tasks
      let response;
      const endpoints = [
        '/api/tasks/all-tasks',
        '/api/tasks/my-tasks',
        '/api/admin/tasks',
        '/api/tasks'
      ];

      for (const endpoint of endpoints) {
        try {
          response = await fetch(
            `${process.env.REACT_APP_BACKEND_BASEURL}${endpoint}`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.tasks) {
              setAllTasks(data.tasks);
              return;
            }
          }
        } catch (err) {
          console.log(`Endpoint ${endpoint} failed, trying next...`);
        }
      }
      
      // If all endpoints fail, create dummy data
      console.log('All task endpoints failed, using dummy data');
      setAllTasks(generateDummyTasks());
      
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setAllTasks(generateDummyTasks());
    }
  };

  const generateDummyTasks = () => {
    const statuses = ['completed', 'in-progress', 'pending'];
    const priorities = ['urgent', 'high', 'medium', 'low'];
    const departments = ['IT', 'HR', 'Marketing', 'Sales'];
    
    const tasks = [];
    for (let i = 1; i <= 20; i++) {
      const createdDate = new Date();
      createdDate.setDate(createdDate.getDate() - Math.floor(Math.random() * 60));
      
      tasks.push({
        _id: `task_${i}`,
        title: `Task ${i} - ${['Website Development', 'Database Design', 'UI/UX Review', 'Testing Phase', 'Documentation'][Math.floor(Math.random() * 5)]}`,
        description: `Description for task ${i}`,
        status: statuses[Math.floor(Math.random() * statuses.length)],
        priority: priorities[Math.floor(Math.random() * priorities.length)],
        createdAt: createdDate.toISOString(),
        deadline: new Date(createdDate.getTime() + (Math.random() * 30 * 24 * 60 * 60 * 1000)).toISOString(),
        assignedTo: {
          _id: `emp_${(i % 3) + 1}`,
          name: ['John Doe', 'Jane Smith', 'Mike Johnson'][i % 3],
          email: ['john@example.com', 'jane@example.com', 'mike@example.com'][i % 3],
          department: departments[Math.floor(Math.random() * departments.length)]
        },
        dailyUpdates: [
          {
            date: new Date().toISOString(),
            workDone: `Work completed for task ${i}`,
            timeSpent: Math.floor(Math.random() * 480) + 60, // 1-8 hours in minutes
            challenges: `Challenges faced in task ${i}`,
            nextDayPlan: `Plans for next day on task ${i}`
          }
        ]
      });
    }
    return tasks;
  };

  const generateReport = async () => {
    try {
      setLoading(true);
      
      // Generate report from available task data
      const generatedReport = generateReportFromTasks(allTasks);
      setReportData(generatedReport);
      
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateReportFromTasks = (tasks) => {
    const startDate = new Date(selectedYear, selectedMonth - 1, 1);
    const endDate = new Date(selectedYear, selectedMonth, 0);
    
    // Filter tasks for selected month
    const monthlyTasks = tasks.filter(task => {
      const taskDate = new Date(task.createdAt);
      return taskDate >= startDate && taskDate <= endDate;
    });

    // Apply additional filters
    let filteredTasks = monthlyTasks;
    
    if (filters.status !== 'all') {
      filteredTasks = filteredTasks.filter(task => task.status === filters.status);
    }
    
    if (filters.priority !== 'all') {
      filteredTasks = filteredTasks.filter(task => task.priority === filters.priority);
    }
    
    if (filters.department !== 'all') {
      filteredTasks = filteredTasks.filter(task => 
        task.assignedTo?.department === filters.department
      );
    }
    
    if (selectedEmployees.length > 0) {
      filteredTasks = filteredTasks.filter(task => 
        task.assignedTo && selectedEmployees.includes(task.assignedTo._id || task.assignedTo)
      );
    }

    // Generate comprehensive report
    const employeeStats = {};
    const departmentStats = {};
    const statusStats = { completed: 0, 'in-progress': 0, pending: 0, cancelled: 0 };
    const priorityStats = { urgent: 0, high: 0, medium: 0, low: 0 };
    
    filteredTasks.forEach(task => {
      // Employee stats
      const employeeId = task.assignedTo?._id || task.assignedTo || 'unassigned';
      const employeeName = task.assignedTo?.name || 'Unassigned';
      
      if (!employeeStats[employeeId]) {
        employeeStats[employeeId] = {
          name: employeeName,
          email: task.assignedTo?.email || '',
          department: task.assignedTo?.department || 'N/A',
          totalTasks: 0,
          completedTasks: 0,
          pendingTasks: 0,
          inProgressTasks: 0,
          totalTimeSpent: 0,
          tasks: []
        };
      }
      
      employeeStats[employeeId].totalTasks++;
      employeeStats[employeeId].tasks.push(task);
      
      // Calculate time spent
      const timeSpent = task.dailyUpdates?.reduce((total, update) => 
        total + (update.timeSpent || 0), 0) || 0;
      employeeStats[employeeId].totalTimeSpent += Math.round(timeSpent / 60); // Convert to hours
      
      // Status counting
      if (task.status === 'completed') {
        employeeStats[employeeId].completedTasks++;
      } else if (task.status === 'in-progress') {
        employeeStats[employeeId].inProgressTasks++;
      } else {
        employeeStats[employeeId].pendingTasks++;
      }
      
      // Department stats
      const dept = task.assignedTo?.department || 'Unknown';
      if (!departmentStats[dept]) {
        departmentStats[dept] = { total: 0, completed: 0, pending: 0 };
      }
      departmentStats[dept].total++;
      if (task.status === 'completed') {
        departmentStats[dept].completed++;
      } else {
        departmentStats[dept].pending++;
      }
      
      // Overall stats
      statusStats[task.status] = (statusStats[task.status] || 0) + 1;
      priorityStats[task.priority] = (priorityStats[task.priority] || 0) + 1;
    });

    return {
      period: {
        month: selectedMonth,
        year: selectedYear,
        monthName: new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long' }),
        startDate: startDate.toDateString(),
        endDate: endDate.toDateString()
      },
      summary: {
        totalTasks: filteredTasks.length,
        totalEmployees: Object.keys(employeeStats).length,
        totalDepartments: Object.keys(departmentStats).length,
        completionRate: filteredTasks.length > 0 ? Math.round((statusStats.completed / filteredTasks.length) * 100) : 0,
        totalTimeSpent: Object.values(employeeStats).reduce((total, emp) => total + emp.totalTimeSpent, 0)
      },
      employeeStats: Object.values(employeeStats),
      departmentStats,
      statusStats,
      priorityStats,
      tasks: filteredTasks
    };
  };

  const downloadPDF = () => {
    if (!reportData) return;
    
    const printContent = generatePrintableReport();
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Monthly Task Report - ${reportData.period.monthName} ${reportData.period.year}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; color: #333; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #667eea; padding-bottom: 20px; }
            .header h1 { color: #667eea; margin: 0; }
            .header p { margin: 10px 0 0 0; color: #666; }
            .summary { background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
            .summary h3 { margin-top: 0; color: #333; }
            .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 20px; }
            .stat-card { background: white; padding: 15px; border-radius: 8px; border: 1px solid #ddd; text-align: center; }
            .stat-card h4 { margin: 0 0 10px 0; color: #667eea; }
            .stat-card .number { font-size: 2rem; font-weight: bold; color: #333; }
            .employee-section { margin-bottom: 30px; }
            .employee-card { background: white; padding: 15px; margin-bottom: 15px; border-radius: 8px; border: 1px solid #ddd; }
            .employee-card h4 { margin: 0 0 10px 0; color: #333; border-bottom: 1px solid #eee; padding-bottom: 5px; }
            .task-item { background: #f8f9fa; padding: 10px; margin: 5px 0; border-radius: 4px; border-left: 4px solid #667eea; }
            .status-completed { color: #28a745; font-weight: bold; }
            .status-in-progress { color: #ffc107; font-weight: bold; }
            .status-pending { color: #dc3545; font-weight: bold; }
            .priority-urgent { color: #dc3545; background: #fed7d7; padding: 2px 6px; border-radius: 4px; }
            .priority-high { color: #fd7e14; background: #feebc8; padding: 2px 6px; border-radius: 4px; }
            .priority-medium { color: #20a8d8; background: #bee3f8; padding: 2px 6px; border-radius: 4px; }
            .priority-low { color: #28a745; background: #c6f6d5; padding: 2px 6px; border-radius: 4px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            th { background-color: #667eea; color: white; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            @media print { body { margin: 0; } .no-print { display: none; } }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const downloadExcel = () => {
    if (!reportData) return;
    
    // Create CSV content
    let csvContent = "data:text/csv;charset=utf-8,";
    
    // Header
    csvContent += `Monthly Task Report - ${reportData.period.monthName} ${reportData.period.year}\n\n`;
    
    // Summary
    csvContent += "SUMMARY\n";
    csvContent += `Total Tasks,${reportData.summary.totalTasks}\n`;
    csvContent += `Total Employees,${reportData.summary.totalEmployees}\n`;
    csvContent += `Completion Rate,${reportData.summary.completionRate}%\n`;
    csvContent += `Total Time Spent,${reportData.summary.totalTimeSpent} hours\n\n`;
    
    // Employee Details
    csvContent += "EMPLOYEE DETAILS\n";
    csvContent += "Name,Email,Department,Total Tasks,Completed,In Progress,Pending,Time Spent (hrs)\n";
    
    reportData.employeeStats.forEach(emp => {
      csvContent += `"${emp.name}","${emp.email}","${emp.department}",${emp.totalTasks},${emp.completedTasks},${emp.inProgressTasks},${emp.pendingTasks},${emp.totalTimeSpent}\n`;
    });
    
    csvContent += "\n\nTASK DETAILS\n";
    csvContent += "Task Title,Assigned To,Status,Priority,Created Date,Deadline,Description\n";
    
    reportData.tasks.forEach(task => {
      const assignedTo = task.assignedTo?.name || 'Unassigned';
      const createdDate = new Date(task.createdAt).toLocaleDateString();
      const deadline = task.deadline ? new Date(task.deadline).toLocaleDateString() : 'No deadline';
      const description = task.description?.replace(/"/g, '""') || '';
      
      csvContent += `"${task.title}","${assignedTo}","${task.status}","${task.priority}","${createdDate}","${deadline}","${description}"\n`;
    });
    
    // Download
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `task_report_${reportData.period.monthName}_${reportData.period.year}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generatePrintableReport = () => {
    if (!reportData) return '';
    
    return `
      <div class="header">
        <h1>Monthly Task Report</h1>
        <p>${reportData.period.monthName} ${reportData.period.year} | Generated on: ${new Date().toLocaleDateString()}</p>
      </div>
      
      <div class="summary">
        <h3>Executive Summary</h3>
        <div class="stats-grid">
          <div class="stat-card">
            <h4>Total Tasks</h4>
            <div class="number">${reportData.summary.totalTasks}</div>
          </div>
          <div class="stat-card">
            <h4>Total Employees</h4>
            <div class="number">${reportData.summary.totalEmployees}</div>
          </div>
          <div class="stat-card">
            <h4>Completion Rate</h4>
            <div class="number">${reportData.summary.completionRate}%</div>
          </div>
          <div class="stat-card">
            <h4>Total Time Spent</h4>
            <div class="number">${reportData.summary.totalTimeSpent}h</div>
          </div>
        </div>
      </div>
      
      <div class="employee-section">
        <h3>Employee Performance Summary</h3>
        ${reportData.employeeStats.map(emp => `
          <div class="employee-card">
            <h4>${emp.name} - ${emp.department}</h4>
            <p><strong>Email:</strong> ${emp.email}</p>
            <p><strong>Task Summary:</strong> ${emp.totalTasks} total | 
               <span class="status-completed">${emp.completedTasks} completed</span> | 
               <span class="status-in-progress">${emp.inProgressTasks} in progress</span> | 
               <span class="status-pending">${emp.pendingTasks} pending</span></p>
            <p><strong>Time Investment:</strong> ${emp.totalTimeSpent} hours</p>
            <p><strong>Completion Rate:</strong> ${emp.totalTasks > 0 ? Math.round((emp.completedTasks / emp.totalTasks) * 100) : 0}%</p>
            
            <div class="tasks">
              <strong>Task Details:</strong>
              ${emp.tasks.slice(0, 5).map(task => `
                <div class="task-item">
                  <strong>${task.title}</strong> - 
                  <span class="status-${task.status}">${task.status.toUpperCase()}</span> - 
                  <span class="priority-${task.priority}">${task.priority.toUpperCase()}</span>
                  <br><small>Created: ${new Date(task.createdAt).toLocaleDateString()}</small>
                </div>
              `).join('')}
              ${emp.tasks.length > 5 ? `<p><em>... and ${emp.tasks.length - 5} more tasks</em></p>` : ''}
            </div>
          </div>
        `).join('')}
      </div>
      
      <div class="task-section">
        <h3>Complete Task List</h3>
        <table>
          <thead>
            <tr>
              <th>Task Title</th>
              <th>Assigned To</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Created</th>
              <th>Deadline</th>
            </tr>
          </thead>
          <tbody>
            ${reportData.tasks.map(task => `
              <tr>
                <td>${task.title}</td>
                <td>${task.assignedTo?.name || 'Unassigned'}</td>
                <td class="status-${task.status}">${task.status.toUpperCase()}</td>
                <td><span class="priority-${task.priority}">${task.priority.toUpperCase()}</span></td>
                <td>${new Date(task.createdAt).toLocaleDateString()}</td>
                <td>${task.deadline ? new Date(task.deadline).toLocaleDateString() : 'No deadline'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const departments = ['IT', 'HR', 'Marketing', 'Sales', 'Finance', 'Operations'];

  return (
    <div className="modal-overlay">
      <div className="report-modal">
        <div className="modal-header">
          <h2>
            <i className="fas fa-chart-bar"></i>
            Monthly Task Report
          </h2>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-content">
          {/* Report Configuration */}
          <div className="report-config">
            <div className="config-section">
              <h3>Report Configuration</h3>
              
              <div className="config-row">
                <div className="config-item">
                  <label>Month:</label>
                  <select 
                    value={selectedMonth} 
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  >
                    {months.map(month => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="config-item">
                  <label>Year:</label>
                  <select 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  >
                    {years.map(year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="config-item">
                  <label>Report Type:</label>
                  <select 
                    value={reportType} 
                    onChange={(e) => setReportType(e.target.value)}
                  >
                    <option value="comprehensive">Comprehensive Report</option>
                    <option value="summary">Summary Only</option>
                    <option value="employee-wise">Employee-wise Report</option>
                  </select>
                </div>
              </div>

              <div className="config-row">
                <div className="config-item">
                  <label>Status Filter:</label>
                  <select 
                    value={filters.status} 
                    onChange={(e) => setFilters({...filters, status: e.target.value})}
                  >
                    <option value="all">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="in-progress">In Progress</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                
                <div className="config-item">
                  <label>Priority Filter:</label>
                  <select 
                    value={filters.priority} 
                    onChange={(e) => setFilters({...filters, priority: e.target.value})}
                  >
                    <option value="all">All Priorities</option>
                    <option value="urgent">Urgent</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                
                <div className="config-item">
                  <label>Department Filter:</label>
                  <select 
                    value={filters.department} 
                    onChange={(e) => setFilters({...filters, department: e.target.value})}
                  >
                    <option value="all">All Departments</option>
                    {departments.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>
              </div>

              {employees.length > 0 && (
                <div className="config-item full-width">
                  <label>Select Employees (Leave empty for all):</label>
                  <div className="employee-selection">
                    {employees.map(emp => (
                      <label key={emp._id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedEmployees.includes(emp._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEmployees([...selectedEmployees, emp._id]);
                            } else {
                              setSelectedEmployees(selectedEmployees.filter(id => id !== emp._id));
                            }
                          }}
                        />
                        {emp.name} ({emp.department || 'No Department'})
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="action-buttons">
              <button 
                className="btn btn-primary" 
                onClick={generateReport}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-chart-line"></i>
                    Generate Report
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Report Preview */}
          {reportData && (
            <div className="report-preview">
              <div className="preview-header">
                <h3>Report Preview</h3>
                <div className="download-buttons">
                  <button className="btn btn-success" onClick={downloadPDF}>
                    <i className="fas fa-file-pdf"></i>
                    Download PDF
                  </button>
                  <button className="btn btn-info" onClick={downloadExcel}>
                    <i className="fas fa-file-excel"></i>
                    Download Excel
                  </button>
                </div>
              </div>

              <div className="report-content">
                <div className="report-header">
                  <h4>Monthly Task Report - {reportData.period.monthName} {reportData.period.year}</h4>
                  <p>Period: {reportData.period.startDate} to {reportData.period.endDate}</p>
                </div>

                {/* Summary Cards */}
                <div className="summary-cards">
                  <div className="summary-card">
                    <div className="card-icon">
                      <i className="fas fa-tasks"></i>
                    </div>
                    <div className="card-content">
                      <h5>Total Tasks</h5>
                      <span className="card-number">{reportData.summary.totalTasks}</span>
                    </div>
                  </div>
                  
                  <div className="summary-card">
                    <div className="card-icon">
                      <i className="fas fa-users"></i>
                    </div>
                    <div className="card-content">
                      <h5>Employees</h5>
                      <span className="card-number">{reportData.summary.totalEmployees}</span>
                    </div>
                  </div>
                  
                  <div className="summary-card">
                    <div className="card-icon">
                      <i className="fas fa-percentage"></i>
                    </div>
                    <div className="card-content">
                      <h5>Completion Rate</h5>
                      <span className="card-number">{reportData.summary.completionRate}%</span>
                    </div>
                  </div>
                  
                  <div className="summary-card">
                    <div className="card-icon">
                      <i className="fas fa-clock"></i>
                    </div>
                    <div className="card-content">
                      <h5>Total Hours</h5>
                      <span className="card-number">{reportData.summary.totalTimeSpent}h</span>
                    </div>
                  </div>
                </div>

                {/* Employee Performance Table */}
                <div className="employee-performance">
                  <h5>Employee Performance</h5>
                  <div className="table-container">
                    <table className="performance-table">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Department</th>
                          <th>Total Tasks</th>
                          <th>Completed</th>
                          <th>In Progress</th>
                          <th>Pending</th>
                          <th>Hours</th>
                          <th>Completion %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.employeeStats.map((emp, index) => (
                          <tr key={index}>
                            <td>
                              <div className="employee-info">
                                <strong>{emp.name}</strong>
                                <small>{emp.email}</small>
                              </div>
                            </td>
                            <td>{emp.department}</td>
                            <td>{emp.totalTasks}</td>
                            <td className="status-completed">{emp.completedTasks}</td>
                            <td className="status-in-progress">{emp.inProgressTasks}</td>
                            <td className="status-pending">{emp.pendingTasks}</td>
                            <td>{emp.totalTimeSpent}h</td>
                            <td>
                              <span className="completion-rate">
                                {emp.totalTasks > 0 ? Math.round((emp.completedTasks / emp.totalTasks) * 100) : 0}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Status and Priority Distribution */}
                <div className="distribution-charts">
                  <div className="chart-section">
                    <h5>Status Distribution</h5>
                    <div className="status-bars">
                      {Object.entries(reportData.statusStats).map(([status, count]) => (
                        <div key={status} className="status-bar">
                          <span className="status-label">{status.replace('-', ' ').toUpperCase()}</span>
                          <div className="bar-container">
                            <div 
                              className={`bar status-${status}`}
                              style={{ width: `${reportData.summary.totalTasks > 0 ? (count / reportData.summary.totalTasks) * 100 : 0}%` }}
                            ></div>
                            <span className="bar-value">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="chart-section">
                    <h5>Priority Distribution</h5>
                    <div className="priority-bars">
                      {Object.entries(reportData.priorityStats).map(([priority, count]) => (
                        <div key={priority} className="priority-bar">
                          <span className="priority-label">{priority.toUpperCase()}</span>
                          <div className="bar-container">
                            <div 
                              className={`bar priority-${priority}`}
                              style={{ width: `${reportData.summary.totalTasks > 0 ? (count / reportData.summary.totalTasks) * 100 : 0}%` }}
                            ></div>
                            <span className="bar-value">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Task Details Section */}
                {reportData.tasks.length > 0 && (
                  <div className="task-details-section">
                    <h5>Task Details ({reportData.tasks.length} tasks)</h5>
                    <div className="task-details-grid">
                      {reportData.tasks.slice(0, 10).map((task, index) => (
                        <div key={index} className="task-detail-card">
                          <div className="task-header">
                            <h6>{task.title}</h6>
                            <div className="task-badges">
                              <span className={`priority-badge ${task.priority}`}>
                                {task.priority}
                              </span>
                              <span className={`status-badge ${task.status}`}>
                                {task.status}
                              </span>
                            </div>
                          </div>
                          <div className="task-meta">
                            <p><strong>Assigned to:</strong> {task.assignedTo?.name || 'Unassigned'}</p>
                            <p><strong>Created:</strong> {new Date(task.createdAt).toLocaleDateString()}</p>
                            {task.deadline && (
                              <p><strong>Deadline:</strong> {new Date(task.deadline).toLocaleDateString()}</p>
                            )}
                            {task.dailyUpdates && task.dailyUpdates.length > 0 && (
                              <p><strong>Time Spent:</strong> {Math.round((task.dailyUpdates.reduce((total, update) => total + (update.timeSpent || 0), 0)) / 60)}h</p>
                            )}
                          </div>
                        </div>
                      ))}
                      {reportData.tasks.length > 10 && (
                        <div className="more-tasks-info">
                          <p>... and {reportData.tasks.length - 10} more tasks</p>
                          <small>Download full report to see all tasks</small>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Department Breakdown */}
                {Object.keys(reportData.departmentStats).length > 0 && (
                  <div className="department-section">
                    <h5>Department Breakdown</h5>
                    <div className="department-grid">
                      {Object.entries(reportData.departmentStats).map(([dept, stats]) => (
                        <div key={dept} className="department-card">
                          <h6>{dept}</h6>
                          <div className="dept-stats">
                            <div className="dept-stat">
                              <span className="stat-label">Total Tasks</span>
                              <span className="stat-value">{stats.total}</span>
                            </div>
                            <div className="dept-stat">
                              <span className="stat-label">Completed</span>
                              <span className="stat-value">{stats.completed}</span>
                            </div>
                            <div className="dept-stat">
                              <span className="stat-label">Completion Rate</span>
                              <span className="stat-value">
                                {stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {reportData === null && !loading && (
            <div className="no-report">
              <div className="no-report-content">
                <i className="fas fa-chart-bar"></i>
                <h3>Generate Your Report</h3>
                <p>Configure your report settings above and click "Generate Report" to see detailed analytics.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthlyTaskReport;